/**
 * Formato canónico de PK ("punto kilométrico"): `km + metros`, con los metros
 * de 1 a 4 dígitos (4 = carreteras re-estacionadas con exceso, p.ej.
 * `235+1200`). Fuente ÚNICA del patrón — antes vivía copiado a mano en 13
 * ficheros con `\d{3,4}`, lo que hacía que la app rechazase valores que ella
 * misma pintaba (ver `pkFormatter` abajo).
 *
 * Los espacios alrededor del `+` son opcionales al escribir; `normalizePk`
 * devuelve siempre la forma canónica con ellos.
 */
export const PK_FORMAT_REGEX = /^\d+\s?\+\s?\d{1,4}$/;

/**
 * Pinta un PK a partir de sus dos enteros (así lo guarda el backend:
 * `pkInitKm` + `pkInitMeter`).
 *
 * Los metros van SIEMPRE a 3 dígitos como mínimo. Sin el relleno, un PK con
 * menos de 100 m salía como `207 + 50` — ambiguo al leerlo (¿50 m o 500 m?) y,
 * sobre todo, rechazado por el propio formulario al reeditarlo. Los metros de
 * 4 dígitos se respetan tal cual (`padStart` no trunca).
 */
export const pkFormatter = (pkKm: number, pkMeter: number, withPlus = true) =>
  `${pkKm}${withPlus ? ' + ' : ''}${String(pkMeter ?? 0).padStart(3, '0')}`;

/**
 * Inversa de `pkFormatter`: parsea un PK "km+metros" (con o sin espacios,
 * p.ej. "82 + 800" o "90+200") a números. Devuelve `null` si el formato no
 * es válido.
 *
 * Tour P5 (2026-07-07): extraído del wizard del RV para que cualquier flujo
 * que hoy hace `pk.replace(/ /g, '').split('+')` a mano (saveData de
 * actuaciones, etc.) pueda converger aquí.
 */
export const parsePk = (pk: string): { km: number; meter: number } | null => {
  const cleaned = pk.replace(/\s+/g, '');
  const match = cleaned.match(/^(\d+)\+(\d{1,4})$/);
  if (!match) return null;
  return { km: Number(match[1]), meter: Number(match[2]) };
};

/**
 * Lleva un PK escrito a mano a la forma canónica: `90+50` → `90 + 050`.
 *
 * Se usa al salir del campo (onBlur) para que el usuario VEA cómo se ha
 * interpretado lo que escribió: los metros son metros, así que `50` son 50 m,
 * no 500. Devuelve `null` si el texto no es un PK — en ese caso el campo se
 * deja como está y habla la validación del schema.
 */
export const normalizePk = (pk: string): string | null => {
  const parsed = parsePk(pk);
  return parsed ? pkFormatter(parsed.km, parsed.meter) : null;
};
